import {
  TellescopeModelFromName,
  useCachedTags,
  usePermissionQuery,
  useTellescopeInfiniteQuery,
} from "@tellescope/tanstack-query";
import { Button, cn, SearchInput } from "@tellescope/ui";
import { useFloatingScrollbar, useDebouncedValue } from "@tellescope/react-hooks";
import { Spinner } from "@tellescope/design-system";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TemplateCard } from "./TemplateCard";

export interface TemplatesViewProps {
  getTemplateOnClick: (
    template: TellescopeModelFromName<"calendar_event_templates">
  ) => (() => void) | { to: string; replace?: boolean };
  getTemplateOnDuplicate: (
    template: TellescopeModelFromName<"calendar_event_templates">
  ) => (() => void) | { to: string; replace?: boolean };
  onDelete: (template: TellescopeModelFromName<"calendar_event_templates">) => void;
  onAddTemplateClick: () => void;
}

export const TemplatesView = ({
  getTemplateOnClick,
  getTemplateOnDuplicate,
  onDelete,
  onAddTemplateClick,
}: TemplatesViewProps) => {
  const { t } = useTranslation("calendar");

  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [debouncedTemplateSearch] = useDebouncedValue(templateSearchQuery);
  const [selectedTemplateTags, setSelectedTemplateTags] = useState<Set<string>>(new Set<string>());

  const { scrollContainerRef, thumbRef, handleScroll } = useFloatingScrollbar({});

  const {
    create: { access: canCreate },
    update: { access: canUpdate },
  } = usePermissionQuery({
    model: "calendar_event_templates",
    actions: ["create", "update"],
  });

  const templatesQuery = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: {
      search: debouncedTemplateSearch
        ? {
            query: debouncedTemplateSearch,
          }
        : undefined,
      limit: 100,
      mdbFilter: selectedTemplateTags.size ? { tags: { $in: Array.from(selectedTemplateTags) } } : {},
    },
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
    placeholderData: (prev) => prev,
  });
  const { data: templateTags } = useCachedTags({ model: "calendar_event_templates" });

  return (
    <>
      <section data-testid="templates-page" className="shrink-0 p-6 border-b flex justify-between items-center">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold">{t("Templates")}</h1>
          <h3 className="font-light text-muted-foreground" data-testid="templates-count">
            {t("{{count}} templates", { count: templatesQuery.data?.length ?? 0 })}
          </h3>
        </div>
        {canCreate && (
          <Button className="font-light" onClick={onAddTemplateClick} data-testid="templates-add-button">
            <Plus />
            {t("Add template")}
          </Button>
        )}
      </section>
      <section className="overflow-y-hidden px-6 pt-6 @container flex flex-col">
        <div className="space-y-3 pb-4 border-b shrink-0">
          <SearchInput
            wrapperClassName="max-w-[400px]"
            placeholder={t("Search templates...")}
            value={templateSearchQuery}
            onChange={(e) => setTemplateSearchQuery(e.target.value)}
            data-testid="templates-search-input"
          />
          <div className="flex items-center gap-2" data-testid="templates-tag-filters">
            {templateTags.map((tag) => (
              <Button
                key={tag}
                data-testid={`templates-tag-filter-${tag}`}
                variant={selectedTemplateTags.has(tag) ? "default" : "outline"}
                className={cn(
                  "rounded-full text-xs font-light h-auto py-1",
                  selectedTemplateTags.has(tag) ? "border" : "text-muted-foreground"
                )}
                onClick={() =>
                  setSelectedTemplateTags((prev) => {
                    const next = new Set(prev);
                    if (prev.has(tag)) {
                      next.delete(tag);
                      return next;
                    } else {
                      next.add(tag);
                      return next;
                    }
                  })
                }
              >
                {tag}
              </Button>
            ))}
            {!!selectedTemplateTags.size && (
              <Button
                variant="ghost"
                className="h-auto py-1 rounded-full text-xs font-light text-muted-foreground"
                onClick={() => setSelectedTemplateTags(new Set<string>())}
                data-testid="templates-tag-filter-clear"
              >
                {t("Clear")}
              </Button>
            )}
          </div>
        </div>
        {templatesQuery.isLoading ? (
          <div className="flex flex-col items-center gap-2 grow" data-testid="templates-loading">
            <Spinner />
            <p className="text-muted-foreground text-sm">{t("Loading...")}</p>
          </div>
        ) : (
          <div className="relative grow min-h-0">
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              data-testid="templates-grid"
              className="grid grid-cols-1 @2xl:grid-cols-2 @4xl:grid-cols-3 @7xl:grid-cols-4 gap-4 py-4 h-full overflow-y-auto [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: "none" }}
            >
              {templatesQuery.data?.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onClick={canUpdate ? getTemplateOnClick(template) : undefined}
                  onDelete={() => onDelete(template)}
                  onDuplicate={getTemplateOnDuplicate(template)}
                />
              ))}
            </div>
            <div
              ref={thumbRef}
              className="absolute top-0 right-1 w-1.5 bg-black/40 rounded-full pointer-events-none opacity-0 transition-opacity duration-300 z-[10000]"
            />
          </div>
        )}
      </section>
    </>
  );
};
